import { Flex } from '@/components/atoms/Grid'
import { FC } from 'react'
import { IconButtonWithBadgeRedesign } from '@/components/molecules/iconButtons/iconButton'
import styled from 'styled-components'
import { SortModel } from '@/network/models/sortModel'
import useTranslation from 'next-translate/useTranslation'
import {
  useFilter,
  useActiveFilterDispatch,
  useActiveFiltersCount
} from '@/providers/filterProvider/filtersProvider'
import { Tiny2 } from '@/components/atoms/typography'
import { Select, SelectLayoutType } from '@/components/molecules/select'
import { FilterQueryItem } from 'types/filterQueryItem'
import { FilterItem } from '@/network/graphql.g'
import { MediaContextProvider } from '@/components/atoms/media'
import { ArrowUpDownIconDefault } from '@/components/atoms/icons/ArrowUpDownIconDefault'
import { FilterIconRedesignDefault } from '@/components/atoms/icons/FilterIconRedesignDefault'

type FilterMobileTabletButtonsProps = {
  sortTypes: FilterItem[]
  activeFiltersCount: number
  onDidTap: (actionType: FilterMobileTabletButtonsActionType) => void
  showCategoryButton: boolean
  showFilterButton: boolean
  showInSale?: boolean
} & Rest

export enum FilterMobileTabletButtonsActionType {
  filter,
  category
}

type FilterSortButtonRedesignProps = {
  showArrowDown?: boolean
  hideText?: boolean
}

const FilterSortButtonRedesign: FC<FilterSortButtonRedesignProps> = ({
  showArrowDown = true,
  hideText = false
}) => {
  const { t } = useTranslation('common')
  const { activeFilters, filter } = useFilter()
  const { toggleFilterAndSetUrl } = useActiveFilterDispatch()
  const sortTypes = filter.sortTypes
  const sortTypesWithPlaceholder: SortModel[] = [...sortTypes]
  const selectedIndex = sortTypesWithPlaceholder.findIndex(
    ({ id }) => activeFilters?.sort?.id === id
  )
  return (
    <StyledSelect
      hideText={hideText}
      height={{ mobile: 32, tablet: 32, desktop: 0 }}
      TextElement={StyledSelectTitle}
      showArrowDown={showArrowDown}
      layoutType={SelectLayoutType.arrowAtEnd}
      SelectIcon={ArrowUpDownIconDefault}
      selectedIndex={selectedIndex > -1 ? selectedIndex : null}
      placeholder={t('Category.Filter.sort')}
      onSelect={(id) =>
        toggleFilterAndSetUrl(id as string, '', FilterQueryItem.SORT)
      }
      items={sortTypesWithPlaceholder.map(({ id, title }) => ({
        id,
        title
      }))}
    />
  )
}

const FilterMobileTabletButtonsRedesign: FC<FilterMobileTabletButtonsProps> = ({
  onDidTap,
  showFilterButton
}) => {
  const { t } = useTranslation('common')
  const count = useActiveFiltersCount()

  return (
    <MediaContextProvider>
      <StyledFlex>
        <FilterSortButtonRedesign hideText={false} />
        {showFilterButton && (
          <StyledIconButtonWithBadge
            badgeCount={count}
            IconElement={<FilterIconRedesignDefault />}
            title={t('Category.Filter.filterButtonTitle')}
            onClick={() => onDidTap(FilterMobileTabletButtonsActionType.filter)}
            data-testid="mobileFilter"
          />
        )}
      </StyledFlex>
    </MediaContextProvider>
  )
}

export default FilterMobileTabletButtonsRedesign

const StyledFlex = styled(Flex)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const StyledIconButtonWithBadge = styled(IconButtonWithBadgeRedesign)`
  padding: 0 13px;
`

const StyledSelectTitle = styled(Tiny2)`
  font-size: 16px;
`

const StyledSelect = styled(Select)`
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.buttonBorderNew};
  min-width: 118px;
`
